import { createSlice } from "@reduxjs/toolkit";
import {
  uploadPrescriptionsAndReports,
  getMyPrescriptions,
  getSelections,
  getLowestPrice,
  getPreviewProviders,
  savePatientLocation,
  createMatchRequest,
  getProviderCount,
  getMatchResults,
  respondToBid,
  patientCounterBid,
  createConversation,
  getConversationById,
  getConversationMessages,
  sendConversationMessage,
  bookAppointment,
  getMyAppointments,
} from "../Thunk/patientFlowThunk";

const initialState = {
  loading: false,
  error: null,
  successMessage: null,
  prescriptions: [],
  selections: [],
  priceInfo: null,
  previewProviders: null,
  patientLocation: null,
  matchRequest: null,
  providerCount: null,
  matches: [],
  matchResultsRaw: null,
  /** True only while `getMatchResults` is in flight (other thunks use `loading`). */
  matchResultsLoading: false,
  /** Latest `matchRequestId` passed to `getMatchResults` / `getProviderCount` (ignore stale fulfill/reject). */
  matchResultsInFlightRequestId: null,
  providerCountInFlightRequestId: null,
  conversation: null,
  messages: [],
  appointment: null,
  appointments: [],
};

const patientFlowSlice = createSlice({
  name: "patientFlow",
  initialState,
  reducers: {
    clearPatientFlowError: (state) => {
      state.error = null;
    },
    clearPatientFlowSuccess: (state) => {
      state.successMessage = null;
    },
    clearMatchResults: (state) => {
      state.matchRequest = null;
      state.providerCount = null;
      state.matches = [];
      state.matchResultsRaw = null;
      state.matchResultsLoading = false;
      state.matchResultsInFlightRequestId = null;
      state.providerCountInFlightRequestId = null;
      state.loading = false;
      state.error = null;
    },
    /** Drop preview list when location scope is incomplete so UI does not show stale “WB-wide” counts. */
    clearPreviewProviders: (state) => {
      state.previewProviders = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadPrescriptionsAndReports.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(uploadPrescriptionsAndReports.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Files uploaded successfully";
        state.prescriptions =
          action.payload?.items ||
          action.payload?.data ||
          action.payload?.prescriptions ||
          [];
      })
      .addCase(uploadPrescriptionsAndReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to upload prescriptions";
      })

      .addCase(getMyPrescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyPrescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions =
          action.payload?.items ||
          action.payload?.data ||
          action.payload?.prescriptions ||
          [];
      })
      .addCase(getMyPrescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch prescriptions";
      })

      .addCase(getSelections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSelections.fulfilled, (state, action) => {
        state.loading = false;
        state.selections =
          action.payload?.selections ||
          action.payload?.data?.selections ||
          action.payload?.items ||
          (Array.isArray(action.payload?.data) ? action.payload.data : null) ||
          [];
      })
      .addCase(getSelections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch selections";
      })

      .addCase(getLowestPrice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLowestPrice.fulfilled, (state, action) => {
        state.loading = false;
        state.priceInfo = action.payload;
      })
      .addCase(getLowestPrice.rejected, (state, action) => {
        state.loading = false;
        const subCategoryId = action.meta?.arg?.subCategoryId;
        // Let patient continue with client-side ₹0–₹30L bounds (normalizePriceInfo) instead of blocking the flow.
        if (subCategoryId) {
          state.error = null;
          state.priceInfo = {
            requestedSubCategoryId: subCategoryId,
            lowestPrice: null,
            highestPrice: null,
            averagePrice: null,
          };
        } else {
          state.error = action.payload || "Failed to fetch price info";
        }
      })

      .addCase(getPreviewProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPreviewProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.previewProviders = action.payload;
      })
      .addCase(getPreviewProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch preview providers";
      })

      .addCase(savePatientLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.patientLocation = action.payload?.location || action.payload || null;
      })

      .addCase(createMatchRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMatchRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.matchRequest = action.payload;
        state.successMessage =
          action.payload?.message || "Bid request submitted successfully";
      })
      .addCase(createMatchRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create match request";
      })

      .addCase(getProviderCount.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.providerCountInFlightRequestId = action.meta.arg;
      })
      .addCase(getProviderCount.fulfilled, (state, action) => {
        const rid = action.meta.arg;
        if (String(rid ?? "") !== String(state.providerCountInFlightRequestId ?? "")) {
          return;
        }
        state.loading = false;
        state.providerCount = action.payload;
      })
      .addCase(getProviderCount.rejected, (state, action) => {
        const rid = action.meta.arg;
        if (String(rid ?? "") !== String(state.providerCountInFlightRequestId ?? "")) {
          return;
        }
        state.loading = false;
        state.error = action.payload || "Failed to fetch provider count";
      })

      .addCase(getMatchResults.pending, (state, action) => {
        state.loading = true;
        state.matchResultsLoading = true;
        state.error = null;
        state.matchResultsInFlightRequestId = action.meta.arg;
      })
      .addCase(getMatchResults.fulfilled, (state, action) => {
        const rid = action.meta.arg;
        if (String(rid ?? "") !== String(state.matchResultsInFlightRequestId ?? "")) {
          return;
        }
        state.loading = false;
        state.matchResultsLoading = false;
        state.matchResultsRaw = action.payload ?? {};
        const fromArrays = [
          ...(Array.isArray(action.payload?.doctors) ? action.payload.doctors : []),
          ...(Array.isArray(action.payload?.hospitals) ? action.payload.hospitals : []),
        ];
        state.matches =
          action.payload?.data ||
          action.payload?.items ||
          action.payload?.matches ||
          action.payload?.bids ||
          fromArrays ||
          [];
      })
      .addCase(getMatchResults.rejected, (state, action) => {
        const rid = action.meta.arg;
        if (String(rid ?? "") !== String(state.matchResultsInFlightRequestId ?? "")) {
          return;
        }
        state.loading = false;
        state.matchResultsLoading = false;
        state.error = action.payload || "Failed to fetch match results";
        if (state.matchResultsRaw == null) {
          state.matchResultsRaw = {};
        }
      })

      .addCase(respondToBid.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(respondToBid.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload?.message || "Bid response submitted";

        const respondedBidId = action.payload?.bidId;
        const respondedAction = action.payload?.action;

        if (respondedBidId) {
          state.matches = state.matches.map((item) => {
            const currentBidId =
              item?.bidId ||
              item?._id ||
              item?.id ||
              item?.bid?._id ||
              item?.bid?.id;

            if (String(currentBidId) !== String(respondedBidId)) {
              return item;
            }

            return {
              ...item,
              patientResponse: respondedAction,
              status:
                respondedAction === "accept"
                  ? "accepted"
                  : respondedAction === "reject"
                  ? "rejected"
                  : item?.status,
            };
          });
        }
      })
      .addCase(respondToBid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to respond to bid";
      })

      .addCase(patientCounterBid.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(patientCounterBid.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Counter offer sent";
      })
      .addCase(patientCounterBid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to counter bid";
      })

      .addCase(createConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.conversation = action.payload;
      })
      .addCase(createConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create conversation";
      })

      .addCase(getConversationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConversationById.fulfilled, (state, action) => {
        state.loading = false;
        state.conversation = action.payload;
      })
      .addCase(getConversationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch conversation";
      })

      .addCase(getConversationMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConversationMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages =
          action.payload?.data ||
          action.payload?.items ||
          action.payload?.messages ||
          [];
      })
      .addCase(getConversationMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch messages";
      })

      .addCase(sendConversationMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendConversationMessage.fulfilled, (state, action) => {
        state.loading = false;
        const msg =
          action.payload?.data ||
          action.payload?.message ||
          action.payload?.item ||
          action.payload;

        if (msg) {
          state.messages.push(msg);
        }
      })
      .addCase(sendConversationMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to send message";
      })

      .addCase(bookAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(bookAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Appointment booked successfully";
        state.appointment = action.payload;
      })
      .addCase(bookAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to book appointment";
      })

      .addCase(getMyAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments =
          action.payload?.data ||
          action.payload?.items ||
          action.payload?.appointments ||
          [];
      })
      .addCase(getMyAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch appointments";
      })

      .addMatcher(
        (action) =>
          action.type.startsWith("patientFlow/") &&
          action.type.endsWith("/pending"),
        (state) => {
          state.error = null;
        }
      );
  },
});

export const {
  clearPatientFlowError,
  clearPatientFlowSuccess,
  clearMatchResults,
  clearPreviewProviders,
} = patientFlowSlice.actions;

export default patientFlowSlice.reducer;
